import React from "react";
import { CreateButton, List, useTable } from "@refinedev/antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Select,
  Skeleton,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import type { CampaignResponse } from "../../api-client";
import type { CrudFilters } from "@refinedev/core";
import { useNavigate } from "react-router-dom";

/**
 * Campaign List Page Component
 *
 * Displays a paginated table of campaigns with server-side pagination.
 * Includes status filter synchronized with URL params.
 */
export const CampaignList: React.FC = () => {
  const navigate = useNavigate();

  const { tableProps, filters, setFilters, tableQueryResult } =
    useTable<CampaignResponse>({
      resource: "campaigns",
      pagination: {
        mode: "server",
      },
      filters: {
        mode: "server",
      },
      syncWithLocation: true,
    });

  const isError = tableQueryResult?.isError ?? false;
  const handleRetry = () => {
    tableQueryResult?.refetch?.();
  };

  const currentStatusFilter =
    filters?.find((filter) =>
      "field" in filter ? filter.field === "status" : false,
    ) ?? null;

  const currentDateFromFilter =
    filters?.find((filter) =>
      "field" in filter
        ? filter.field === "created_at" && filter.operator === "gte"
        : false,
    ) ?? null;

  const currentDateToFilter =
    filters?.find((filter) =>
      "field" in filter
        ? filter.field === "created_at" && filter.operator === "lte"
        : false,
    ) ?? null;

  const currentDateRange =
    currentDateFromFilter && currentDateToFilter
      ? [
          dayjs(currentDateFromFilter.value as string),
          dayjs(currentDateToFilter.value as string),
        ]
      : undefined;

  const handleStatusChange = (value: CampaignResponse["status"] | "all") => {
    setFilters((previousFilters: CrudFilters) => {
      const otherFilters = (previousFilters ?? []).filter(
        (filter) => ("field" in filter ? filter.field : undefined) !== "status",
      );

      if (value === "all") {
        return [...otherFilters];
      }

      return [
        ...otherFilters,
        {
          field: "status",
          operator: "eq",
          value,
        },
      ];
    });
  };

  const handleDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setFilters((previousFilters: CrudFilters) => {
      const otherFilters = (previousFilters ?? []).filter(
        (filter) =>
          "field" in filter ? filter.field !== "created_at" : true,
      );

      if (!dates || !dates[0] || !dates[1]) {
        return [...otherFilters];
      }

      return [
        ...otherFilters,
        {
          field: "created_at",
          operator: "gte",
          value: dates[0].startOf("day").toISOString(),
        },
        {
          field: "created_at",
          operator: "lte",
          value: dates[1].endOf("day").toISOString(),
        },
      ];
    });
  };

  const { RangePicker } = DatePicker;

  const isLoading = tableProps.loading && !isError;

  const skeletonRows: CampaignResponse[] = React.useMemo(
    () =>
      Array.from({ length: tableProps.pagination?.pageSize ?? 10 }).map(
        (_, index) => ({
          id: `skeleton-${index}`,
          title: "",
          rule_code: "",
          status: "draft",
          created_at: new Date().toISOString(),
          auto_generate_tasks: false,
          message_template: null,
          image_data: null,
          error: null,
          started_at: null,
          finished_at: null,
        }),
      ),
    [tableProps.pagination?.pageSize],
  );

  const dataSource = isError
    ? []
    : isLoading
      ? skeletonRows
      : tableProps.dataSource;

  const columns: ColumnsType<CampaignResponse> = [
    {
      title: "Название",
      dataIndex: "title",
      key: "title",
      render: (value: string) => {
        if (isLoading) {
          return <Skeleton.Button active size="small" style={{ width: 160 }} />;
        }

        return value;
      },
    },
    {
      title: "Тип кампании",
      dataIndex: "rule_code",
      key: "rule_code",
      render: (value: string) => {
        if (isLoading) {
          return <Skeleton.Button active size="small" style={{ width: 140 }} />;
        }

        return value;
      },
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (status: CampaignResponse["status"]) => {
        if (isLoading) {
          return <Skeleton.Button active size="small" style={{ width: 80 }} />;
        }

        const statusColor: Record<CampaignResponse["status"], string> = {
          draft: "default",
          active: "processing",
          done: "success",
          cancelled: "warning",
          failed: "error",
        };

        const statusAriaLabels: Record<CampaignResponse["status"], string> = {
          draft: "Статус: Черновик",
          active: "Статус: Активна",
          done: "Статус: Завершена",
          cancelled: "Статус: Отменена",
          failed: "Статус: Ошибка",
        };

        return (
          <Tag
            color={statusColor[status] ?? "default"}
            icon={status === "failed" ? <ExclamationCircleOutlined /> : undefined}
            aria-label={statusAriaLabels[status]}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Создана",
      dataIndex: "created_at",
      key: "created_at",
      render: (value: string) => {
        if (isLoading) {
          return <Skeleton.Button active size="small" style={{ width: 160 }} />;
        }

        return dayjs(value).format("DD.MM.YYYY HH:mm");
      },
    },
  ];

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div>Кампании пока не созданы</div>
          <Typography.Text type="secondary" style={{ margin: 0 }}>
            Создайте первую кампанию, чтобы запустить коммуникации с клиентами
          </Typography.Text>
        </div>
      }
    >
      <CreateButton type="primary" resource="campaigns">
        Создать первую кампанию
      </CreateButton>
    </Empty>
  );

  return (
    <List
      title="Кампании"
      headerButtons={
        <CreateButton resource="campaigns">Создать кампанию</CreateButton>
      }
    >
      {isError ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Не удалось загрузить список кампаний"
          description="Проверьте подключение и повторите попытку."
          action={
            <Button type="primary" onClick={handleRetry} icon={<ReloadOutlined />}>
              Повторить попытку
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Select
            allowClear={false}
            style={{ width: 220 }}
            options={[
              { label: "Все статусы", value: "all" },
              { label: "Черновик", value: "draft" },
              { label: "Активна", value: "active" },
              { label: "Завершена", value: "done" },
              { label: "Отменена", value: "cancelled" },
              { label: "Ошибка", value: "failed" },
            ]}
            placeholder="Фильтр по статусу"
            value={currentStatusFilter ? currentStatusFilter.value : "all"}
            onChange={handleStatusChange}
          />
          <RangePicker
            value={currentDateRange}
            onChange={handleDateRangeChange}
            allowClear
            format="DD.MM.YYYY"
            placeholder={["Дата начала", "Дата окончания"]}
          />
        </div>
      </div>
      <Table
        {...tableProps}
        dataSource={dataSource}
        rowKey="id"
        columns={columns}
        loading={isLoading}
        pagination={tableProps.pagination}
        locale={{ emptyText: emptyState }}
        onRow={(record) =>
          isLoading
            ? {}
            : {
                onClick: () => navigate(`/campaigns/show/${record.id}`),
                style: { cursor: "pointer" },
              }
        }
      />
    </List>
  );
};

export default CampaignList;
